import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';

import { cx } from './classNames';
import { resolveIconClass } from './icons';
import { Portal } from './Portal';
import type { DropDownItem } from './types';
import { useOutsideDismiss, usePopupPosition } from './usePopupPosition';

/** Pendant zu `SfDropDownButton` mit `DropDownMenuItem`-Einträgen. */
export interface DropDownButtonProps {
  content?: string;
  iconClass?: string;
  disabled?: boolean;
  /** Entspricht `CssClass` und landet wie dort auf der Schaltfläche. */
  cssClass?: string;
  items: DropDownItem[];
  onItemSelected: (item: DropDownItem) => void;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function DropDownButton(props: DropDownButtonProps) {
  const { items, onItemSelected } = props;
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useOutsideDismiss(open, close, [buttonRef, popupRef]);
  const popupStyle = usePopupPosition({ open, anchorRef: buttonRef, popupRef });

  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  const select = (index: number) => {
    const item = items[index];
    setOpen(false);
    buttonRef.current?.focus();
    if (item !== undefined) onItemSelected(item);
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedIndex((index) => (index + 1) % Math.max(items.length, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedIndex((index) => (index - 1 + items.length) % Math.max(items.length, 1));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      select(focusedIndex);
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    }
  };

  const icon = resolveIconClass(props.iconClass);
  const iconOnly = props.content === undefined || props.content === '';

  return (
    <>
      <button
        id={props.id}
        ref={buttonRef}
        type="button"
        title={props.title}
        style={props.style}
        disabled={props.disabled === true}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={iconOnly ? props.title : undefined}
        className={cx('e-btn', 'e-dropdown-btn', props.cssClass, props.className)}
        onClick={() => {
          setOpen((value) => !value);
          setFocusedIndex(0);
        }}
      >
        {icon === undefined ? null : (
          <span className={cx('e-btn-icon', 'e-icons', iconOnly && 'e-icon-only', icon)} />
        )}
        {props.content}
        <span className="e-btn-icon e-icons e-caret fa-solid fa-caret-down" />
      </button>
      {open ? (
        <Portal>
          <div ref={popupRef} className="e-dropdown-popup" style={popupStyle}>
            <ul ref={listRef} role="menu" tabIndex={-1} onKeyDown={onListKeyDown}>
              {items.map((item, index) => {
                const itemIcon = resolveIconClass(item.iconClass);
                return (
                  <li
                    key={item.id}
                    role="menuitem"
                    className={cx('e-item', index === focusedIndex && 'e-selected')}
                    onMouseEnter={() => {
                      setFocusedIndex(index);
                    }}
                    onClick={() => {
                      select(index);
                    }}
                  >
                    {itemIcon === undefined ? null : (
                      <span className={cx('e-menu-icon', 'e-icons', itemIcon)} />
                    )}
                    {item.text}
                  </li>
                );
              })}
            </ul>
          </div>
        </Portal>
      ) : null}
    </>
  );
}

export default DropDownButton;
